// frontend/src/pages/Courses.tsx

import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCourses } from '../hooks/useCourses';
import { CourseFilterTabs } from '../components/courses/CourseFilterTabs';
import { CourseCard } from '../components/courses/CourseCard';
import { CourseCategory } from '../types/courses.types';
import { useAuthStore } from '../store/authStore';
import '../styles/courses.css';

const CAT_TITLES: Record<string, string> = {
  all: 'Tüm Dersler 📚',
  python: 'Python Dersleri 🐍',
  matematik: 'Matematik Dersleri 🧮',
  fen: 'Fen Bilimleri 🔬',
  ingilizce: 'İngilizce Dersleri 🇬🇧',
  turkce: 'Türkçe Dersleri 📖',
  yapay_zeka: 'Yapay Zeka Dersleri 🤖',
  web: 'Web Dersleri 🌐',
  game: 'Oyun Geliştirme 🎮',
  veri_analizi: 'Veri Analizi Dersleri 📊',
};

type AgeFilter = 'all' | '7-9' | '10-13' | '14-17';

const AGE_TABS: { value: AgeFilter; label: string; icon: string; color: string }[] = [
  { value: 'all',   label: 'Tüm Yaşlar', icon: '🌟', color: 'var(--primary)' },
  { value: '7-9',   label: '7-9 Yaş',    icon: '🐣', color: '#10b981' },
  { value: '10-13', label: '10-13 Yaş',  icon: '🚀', color: '#f59e0b' },
  { value: '14-17', label: '14-17 Yaş',  icon: '🎓', color: '#8b5cf6' },
];

const AGE_GROUP_LABELS: Record<string, { title: string; color: string; bg: string }> = {
  '7-9':   { title: '7-9 Yaş Grubu 🐣',  color: '#065f46', bg: '#d1fae5' },
  '10-13': { title: '10-13 Yaş Grubu 🚀', color: '#78350f', bg: '#fef3c7' },
  '14-17': { title: '14-17 Yaş Grubu 🎓', color: '#4c1d95', bg: '#ede9fe' },
  'genel': { title: 'Genel & Diğer 🌟',   color: '#1e3a5f', bg: '#dbeafe' },
};

/** Kullanıcının yaşına göre hangi kategoriye girdiğini belirler */
function getAgeCategoryFromAge(age: number | null | undefined): AgeFilter {
  if (!age) return 'all';
  if (age >= 7  && age <= 9)  return '7-9';
  if (age >= 10 && age <= 13) return '10-13';
  if (age >= 14 && age <= 17) return '14-17';
  return 'all';
}

export function CoursesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { courses, isLoading, error } = useCourses();
  const profile = useAuthStore(s => s.profile);

  // Kullanıcının yaşına göre otomatik kategori
  const userAgeCategory = useMemo(() => getAgeCategoryFromAge(profile?.age), [profile?.age]);

  const catParam = searchParams.get('cat') as CourseCategory | null;
  const ageParam = searchParams.get('age') as AgeFilter | null;

  const [activeFilter, setActiveFilter] = useState<CourseCategory | 'all'>(catParam ?? 'all');

  // URL'de age parametresi yoksa kullanıcının yaş kategorisini default al
  const [activeAge, setActiveAge] = useState<AgeFilter>(ageParam ?? userAgeCategory);

  // URL değişince state'leri güncelle
  useEffect(() => { setActiveFilter(catParam ?? 'all'); }, [catParam]);
  useEffect(() => {
    // URL'de parametre varsa onu kullan; yoksa kullanıcı yaşını kullan
    setActiveAge(ageParam ?? userAgeCategory);
  }, [ageParam, userAgeCategory]);

  function handleFilterChange(val: CourseCategory | 'all') {
    setActiveFilter(val);
    const next = new URLSearchParams(searchParams);
    if (val === 'all') next.delete('cat'); else next.set('cat', val);
    setSearchParams(next);
  }

  function handleAgeChange(val: AgeFilter) {
    setActiveAge(val);
    const next = new URLSearchParams(searchParams);
    if (val === 'all') next.delete('age'); else next.set('age', val);
    setSearchParams(next);
  }

  // Kategori + yaş filtresi birleşik uygula
  const filteredCourses = useMemo(() => {
    return courses.filter(c => {
      const catMatch = activeFilter === 'all' || c.category === activeFilter;
      const ageMatch = activeAge === 'all' || c.age_category === activeAge;
      return catMatch && ageMatch;
    });
  }, [courses, activeFilter, activeAge]);

  // Yaş "Tümü" seçiliyse gruplu göster; belirli yaş seçiliyse düz liste
  const groupedCourses = useMemo(() => {
    if (activeAge !== 'all') return null;
    const groups: Record<string, typeof filteredCourses> = {
      '7-9': [], '10-13': [], '14-17': [], 'genel': [],
    };
    filteredCourses.forEach(c => {
      if      (c.age_category === '7-9')   groups['7-9'].push(c);
      else if (c.age_category === '10-13') groups['10-13'].push(c);
      else if (c.age_category === '14-17') groups['14-17'].push(c);
      else                                  groups['genel'].push(c);
    });
    return groups;
  }, [filteredCourses, activeAge]);

  const title = CAT_TITLES[activeFilter] ?? 'Dersler 📚';

  // Otomatik yaş filtresi aktif mi? (URL'de parametre yoktu, profil yaşından geldi)
  const isAutoAgeFilter = !ageParam && userAgeCategory !== 'all' && activeAge === userAgeCategory;

  return (
    <div className="courses-page">
      <header className="courses-header">
        <h1>{title}</h1>
        <p>İlgi alanına ve yaş grubuna göre filtrele, öğrenmeye başla</p>
      </header>

      {/* Konu filtresi */}
      <CourseFilterTabs active={activeFilter} onChange={handleFilterChange} />

      {/* ── Yaş grubu filtresi ── */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12, marginBottom: 16 }}>
        {AGE_TABS.map(tab => {
          const isActive = activeAge === tab.value;
          return (
            <button
              key={tab.value}
              onClick={() => handleAgeChange(tab.value)}
              style={{
                padding: '7px 18px',
                borderRadius: '99px',
                border: `2px solid ${isActive ? tab.color : 'var(--border)'}`,
                background: isActive ? tab.color : 'var(--bg-card)',
                color: isActive ? '#fff' : 'var(--text-secondary)',
                fontFamily: 'inherit',
                fontSize: '13px',
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                boxShadow: isActive ? `0 3px 12px ${tab.color}50` : 'none',
                transform: isActive ? 'translateY(-1px)' : 'none',
              }}
            >
              {tab.icon} {tab.label}
              {/* Kullanıcının kendi yaş kategorisine "Senin için" badge'i */}
              {tab.value === userAgeCategory && tab.value !== 'all' && (
                <span style={{
                  fontSize: 10, fontWeight: 800, letterSpacing: '0.02em',
                  background: isActive ? 'rgba(255,255,255,0.25)' : tab.color,
                  color: isActive ? '#fff' : '#fff',
                  padding: '1px 7px', borderRadius: 99,
                }}>
                  Senin için
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── Otomatik yaş filtresi bildirimi ── */}
      {isAutoAgeFilter && profile?.age && (() => {
        const info = AGE_GROUP_LABELS[activeAge];
        return (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            background: `linear-gradient(135deg, ${info.bg}, ${info.bg}dd)`,
            border: `1.5px solid ${info.color}25`,
            borderRadius: 14, padding: '12px 18px', marginBottom: 20,
            boxShadow: `0 2px 12px ${info.color}15`,
          }}>
            <div style={{
              width: 40, height: 40, borderRadius: '50%',
              background: `${info.color}15`, display: 'flex',
              alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0,
            }}>
              {activeAge === '7-9' ? '🐣' : activeAge === '10-13' ? '🚀' : '🎓'}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 800, color: info.color, fontSize: 14 }}>
                {profile.full_name?.split(' ')[0] || 'Sana'} için özel kurslar gösteriliyor
              </div>
              <div style={{ fontSize: 12, color: info.color, opacity: 0.75, marginTop: 2 }}>
                {profile.age} yaşındaki öğrenciler için hazırlanmış {filteredCourses.length} kurs • {info.title}
              </div>
            </div>
            <button
              onClick={() => handleAgeChange('all')}
              style={{
                background: 'none', border: `1.5px solid ${info.color}50`,
                color: info.color, padding: '5px 14px',
                borderRadius: 99, cursor: 'pointer',
                fontWeight: 700, fontSize: 12, flexShrink: 0,
                transition: 'all 0.15s',
              }}
            >
              Tümünü Gör
            </button>
          </div>
        );
      })()}

      {/* Manuel yaş filtresi bilgi bandı (kullanıcı kendisi seçtiyse) */}
      {activeAge !== 'all' && !isAutoAgeFilter && (() => {
        const info = AGE_GROUP_LABELS[activeAge];
        return (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: info.bg, border: `1.5px solid ${info.color}30`,
            borderRadius: 12, padding: '10px 16px', marginBottom: 20,
          }}>
            <span style={{ fontSize: 20 }}>
              {activeAge === '7-9' ? '🐣' : activeAge === '10-13' ? '🚀' : '🎓'}
            </span>
            <div>
              <div style={{ fontWeight: 700, color: info.color, fontSize: 14 }}>
                {info.title} gösteriliyor
              </div>
              <div style={{ fontSize: 12, color: info.color, opacity: 0.75 }}>
                {filteredCourses.length} kurs bulundu
              </div>
            </div>
            <button
              onClick={() => handleAgeChange('all')}
              style={{
                marginLeft: 'auto', background: 'none',
                border: `1.5px solid ${info.color}60`,
                color: info.color, padding: '4px 12px',
                borderRadius: 99, cursor: 'pointer',
                fontWeight: 700, fontSize: 12,
              }}
            >
              ✕ Filtreyi Kaldır
            </button>
          </div>
        );
      })()}

      {isLoading && <div className="courses-loading">Yükleniyor...</div>}
      {error     && <div className="courses-error">Bir şeyler ters gitti: {error}</div>}

      {!isLoading && !error && filteredCourses.length === 0 && (
        <div className="courses-empty">
          Bu filtreye uygun henüz ders bulunmuyor.
          {(activeAge !== 'all' || activeFilter !== 'all') && (
            <button
              onClick={() => { handleFilterChange('all'); handleAgeChange('all'); }}
              style={{
                display: 'block', marginTop: 12, padding: '8px 20px',
                borderRadius: 99, border: '1.5px solid var(--primary)',
                background: 'transparent', color: 'var(--primary)',
                cursor: 'pointer', fontWeight: 700,
              }}
            >
              Tüm filtreleri kaldır
            </button>
          )}
        </div>
      )}

      {!isLoading && !error && filteredCourses.length > 0 && (
        <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 40 }}>

          {/* Belirli yaş seçiliyse — 5'li grid */}
          {activeAge !== 'all' ? (
            <div className="courses-grid">
              {filteredCourses.map(course => (
                <CourseCard key={course.id} course={course} />
              ))}
            </div>
          ) : (
            /* Tüm yaşlar seçiliyse — gruplu görünüm */
            groupedCourses && Object.entries(groupedCourses).map(([key, list]) => {
              if (list.length === 0) return null;
              const info = AGE_GROUP_LABELS[key];
              return (
                <section key={key} className="courses-age-section">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                    <h2 style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>
                      {info.title}
                    </h2>
                    <span style={{
                      fontSize: 12, fontWeight: 700,
                      background: info.bg, color: info.color,
                      padding: '3px 10px', borderRadius: 12,
                    }}>
                      {list.length} Kurs
                    </span>
                    {key !== 'genel' && (
                      <button
                        onClick={() => handleAgeChange(key as AgeFilter)}
                        style={{
                          marginLeft: 'auto', background: 'none',
                          border: `1.5px solid ${info.color}`,
                          color: info.color, padding: '4px 14px',
                          borderRadius: 99, cursor: 'pointer',
                          fontSize: 12, fontWeight: 700,
                          transition: 'all 0.15s',
                        }}
                      >
                        Sadece {key} Yaş →
                      </button>
                    )}
                  </div>
                  <div className="courses-grid">
                    {list.map(course => (
                      <CourseCard key={course.id} course={course} />
                    ))}
                  </div>
                </section>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
